import { type AvatarSubject, Avatar } from '@/components/Avatar'

import { IconButton } from '@/components/IconButton'
import { Tooltip } from '@/components/Tooltip'
import styles from './ConversationHeader.module.css'

export interface ConversationHeaderProps {
  online: boolean
  avatar?: AvatarSubject
  displayName?: string
  showLastSeen: boolean
  /**
   * `null` when nobody has claimed the conversation yet.
   */
  lastSeenText: string | null
  canRefresh: boolean
  onRefresh: () => unknown
  muted: boolean
  onToggleMute: () => unknown
  canDownload: boolean
  onDownload: () => unknown
}

export function ConversationHeader({
  online,
  avatar,
  displayName,
  showLastSeen,
  lastSeenText,
  canRefresh,
  onRefresh,
  muted,
  onToggleMute,
  canDownload,
  onDownload,
}: ConversationHeaderProps) {
  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.participant}>
          <div className={styles.avatarContainer}>
            <Avatar
              badge={online ? 'online' : 'offline'}
              size={38}
              subject={avatar}
            />
          </div>
          <div>
            <div className={styles.displayName}>{displayName}</div>
            {showLastSeen && (
              <div className={styles.lastSeen}>
                {lastSeenText !== null
                  ? lastSeenText === 'now'
                    ? 'Active now'
                    : `Last seen ${lastSeenText} ago`
                  : 'Unclaimed'}
              </div>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          {canRefresh && (
            <Tooltip content="Refresh conversation" placement="bottom">
              <IconButton
                name="loader-arrows"
                label="Refresh conversation"
                variant="ghost"
                onPress={onRefresh}
              />
            </Tooltip>
          )}
          <Tooltip
            content={muted ? 'Unmute conversation' : 'Mute conversation'}
            placement="bottom"
          >
            <IconButton
              name={muted ? 'notifications-thick-muted' : 'notifications-thick'}
              label={muted ? 'Unmute conversation' : 'Mute conversation'}
              variant="ghost"
              onPress={onToggleMute}
            />
          </Tooltip>
          {canDownload && (
            <Tooltip content="Download conversation history" placement="bottom">
              <IconButton
                name="download"
                label="Download conversation history"
                variant="ghost"
                onPress={onDownload}
                testId={`download-conversation-new`}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}
